import { useCallback, useState } from 'react';

import { accountDeletionApi, profileApi } from '@pukka/api';
import auth from '@pukka/auth';
import { assets } from '@pukka/common';
import { assertUtil } from '@pukka/common/utils';
import { ToastContainer, toast } from '@pukka/toast';
import { Button, Shimmer, Text } from '@pukka/ui-components';

import { AccountDeletionForm } from './AccountDeletionForm';

function AccountDeletionPage() {
  const { data: profileData, isLoading } = profileApi.useProfile();
  const isOwner = profileData?.is_owner;

  const [ticketNumber, setTicketNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const goToLegacy = useCallback(() => {
    window.location.href = '/';
  }, []);

  const handleSignOut = useCallback(() => {
    auth.logout();
  }, []);

  const handleSubmit = async (reason: string, notes: string) => {
    try {
      setIsSubmitting(true);

      const res = await accountDeletionApi.createAccountDeletionTicket({
        reason,
        notes,
      });

      setTicketNumber(res.ticket_number);

      auth.logout(undefined, true);
    } catch (error: unknown) {
      assertUtil.assertObjectIsTruthy<{
        response: {
          data: {
            message: string;
            code: string;
          };
        };
      }>(error);

      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative h-screen w-screen bg-neutral-200">
      <ToastContainer closeOnClick newestOnTop={false} hideProgressBar />
      <header className="flex h-14 w-full items-center justify-between bg-legacy-500 px-4">
        <div className="mx-auto flex w-[1000px] items-center justify-between ">
          <a href="/">
            <img alt="Moka logo" width={40} height={40} src={assets.logoMoka} />
          </a>
          <div className="flex gap-6">
            <a
              href="https://help.mokapos.com/s/?language=in"
              target="_blank"
              className="text-neutral-400 hover:text-white"
              rel="noreferrer"
            >
              HELP
            </a>
            <button type="button" onClick={handleSignOut} className="text-neutral-400 hover:text-white">
              SIGN OUT
            </button>
          </div>
        </div>
      </header>
      <div className="flex items-center justify-center bg-neutral-200 pt-10">
        <div className="flex max-w-144 flex-col gap-5 rounded-xl bg-white px-12 py-16 text-center">
          <Text variant="title-2" weight="bold">
            Account Deletion Request{ticketNumber ? ' Sent' : ''}
          </Text>
          {isLoading && (
            <div className="flex flex-col items-center">
              <Shimmer size="md" className="w-[480px]" />
              <Shimmer size="md" className="w-96" />
              <Shimmer size="xxl" className="mt-5 w-full" />
            </div>
          )}
          {!isLoading && !isOwner && (
            <>
              <Text>To close or delete your account permanently, please submit a request from master/owner account.</Text>
              <Button onClick={goToLegacy}>Close</Button>
            </>
          )}
          {!isLoading && isOwner && !!ticketNumber && (
            <>
              <Text>
                Your account deletion request has been received (ticket number <b>#{ticketNumber}</b>). Please keep this ticket number and
                use it for any inquiries. You&apos;ll be logged out from all devices and lose access to your account.
              </Text>
              <Button onClick={handleSignOut}>Close</Button>
            </>
          )}

          {!isLoading && isOwner && !ticketNumber && <AccountDeletionForm isSubmitting={isSubmitting} onSubmit={handleSubmit} />}
        </div>
      </div>
    </div>
  );
}

export { AccountDeletionPage };
